import Urls from "oc-commons/Urls.js";
import { defaultCatchAsError, defaultCatchAsWarning } from "oc-ui/errorHandling.js";
import Mimetypes from "oc-commons/Mimetypes";
import UrlFluid from "oc-commons/UrlFluid";
import I18N from "oc-I18N";
import Commons from "oc-commons/Commons";

/**
 * Kapselt die Low-Level-Rest-API zum WKFG2 - Backend. Die Methoden geben jeweils Promises zurück.
 *
 * Für die meisten Zwecke innerhalb der APP sollte man statt dessen die Wkfg2BackendExtension verwenden,
 * weil dort ganze Workflows wie das Speichern einer Konfiguration mit Erzeugen aller Snapshots sowie die
 * APP-Events abgebildet sind.
 *
 * Typischer Aufruf:
 *
 * <pre><code>
 * const backendApi = new Wkfg2BackendApi(APP.settings.wkfgBackendUrl);
 * backendApi.loadMetadata().then((metadata)=>{
 *     console.log('Das komplette Produktmodell:',metadata);
 * });
 * </code></pre>
 *
 * Falls der Ajax-Aufruf fehlschlägt, wird defaultmäßig ein Fehlerdialog angezeigt.
 * Falls man statt dessen eine eigene Fehlerbehandlung durchführen möchte, benutzt man die Promise-catch-Methode:
 *
 * <pre><code>
 * const backendApi = new Wkfg2BackendApi(APP.settings.wkfgBackendUrl);
 * backendApi.loadConfiguration('id=1').then(configuration=>{
 *    console.log(configuration);
 * }).catch(reason=>{
 *    console.log('Den Fehler behandele ich selbst und zeige keinen Fehlerdialog an! Fehler: ',reason);
 * });
 * </code></pre>
 *
 * Das Angeben einer Default-Fehleraktion und normales Debuggen von Fehlern innerhalb von then/catch-Methoden
 * ist kein Standard-Verhalten von Promises, sondern eine holodeck-Erweiterung. Siehe dazu oc-commons/Promise.
 */

export default class Wkfg2BackendApi {
    /**
     * Creates an instance of Wkfg2BackendApi.
     * @param {string} wkfgBackendUrl
     * @param {?Wkfg2TypeConverter} wkfgTypeConverter
     * @param {?MetadataLoadMapper} falls gesetzt, wird dieser statt des wkfgTypeConverter zum Laden des metadata verwendet
     */
    constructor(
        wkfgBackendUrl,
        wkfgTypeConverter,
        metadataLoadMapper = (metadata) =>
            this.wkfgTypeConverter ? this.wkfgTypeConverter.parse(metadata) : metadata
    ) {
        this.wkfgBackendUrl = wkfgBackendUrl;
        this.wkfgTypeConverter = wkfgTypeConverter;
        this.metadataLoadMapper = metadataLoadMapper;
    }

    /**
     * Lädt die Beschreibung des Metamodells und der Metadaten in einem, und gibt ein Promise zurück.
     * Hinweis: Best Practise ist es, die I18N-Texte nicht über diesen Weg, sondern direkt als Teil der HTML-Seite auszuliefern,
     * damit z.B. der Loading-Screen oder Fehlermeldungen in der richtigen Sprache erscheinen.
     * @returns {Promise} {
     *    Konfiguration: {
     *        id: 1,
     *        Code: "MBN443QTX",
     *        ...
     *    },
     *    metadata: {
     *        MaterialPbr: [
     *          {
     *              id: 1,
     *              name: "Leder",
     *              ...
     *          },
     *          ...
     *        ]
     *    },
     *    metamodell: {
     *       languagesI18N: ["de","en"],
     *       tables: [
     *          {
     *              name: "Konfiguration",
     *              label: "Konfigurationen",
     *              ...
     *          },
     *          ...
     *       }
     *    },
     *    i18n_json: {
     *       "__shortname": "de",
     *       "number_dec_point": ",",
     *       "3 pro Seite": "3 pro Seite",
     *       ...
     *    },
     *    content_url: 'http://localhost/wp-content/uploads/wkfgcontent',
     * }
     */
    loadConfigData({
        code = null,
        metamodell = true,
        i18n = true,
        settings = true,
        metadata = true,
        tableMetaTags = null,
        tenant = null,
        lang = null,
    }) {
        let url = new UrlFluid(this.wkfgBackendUrl).addPath("metamodell/config");
        url.param("Code", code);
        url.param("metamodel", metamodell);
        url.param("metadata", metadata);
        url.param("settings", settings);
        url.param("i18n", i18n);
        if (!Commons.isEmpty(tableMetaTags)) url.param("tableMetaTags", tableMetaTags);
        [
            ["tenant", tenant],
            ["lang", lang],
        ].forEach(([key, value]) => {
            if (Commons.isEmpty(value)) {
                value = Urls.urlParam(key);
            }
            if (!Commons.isEmpty(value)) {
                url.param(key, value);
            }
        });
        return defaultCatchAsError($.getJSON(url)).then(async (config) => {
            if (config.i18n_json) {
                I18N.setKeyValueMap(config.i18n_json);
            }
            APP.metamodel = config.metamodel;

            config.metadata = await this.metadataLoadMapper(config.metadata);

            if (config.settings) {
                Commons.defaultsDeepSafe(APP.settings, config.settings);
            }

            if (this.wkfgTypeConverter) {
                if (APP.metamodel?.settings) {
                    // Diese Settings kommen aus base.metamodell.js ganz unten, diese dürfen die Settings aus global_js_constants nicht überschreiben (darauf muss man selbst achten!)!
                    this.wkfgTypeConverter.parseSettings(APP.settings, APP.metamodel.settings);
                }
                if (config.metadata?.Settings) {
                    // Das sind die Settings aus der Backend-Tabelle Settings, auch diese dürfen die Settings aus global_js_constants nicht überschreiben (darauf muss man selbst achten!)!
                    this.wkfgTypeConverter.parseSettings(APP.settings, config.metadata.Settings);
                }
            }

            return config;
        });
    }

    /**
     * Lädt die Beschreibung des Metamodells - also den Inhalt der metamodel.json, und gibt ein Promise zurück
     * @returns {Promise} Promise mit dem Inhalt der JSON-Datei
     */
    loadMetamodel() {
        const url = this.wkfgBackendUrl + "metamodell/json";
        return defaultCatchAsError($.getJSON(url));
    }

    /**
     * Lädt die Metadata (meist die relationalen Daten der Produkte, Materialien, etc.)
     * @param {string} [query='tableMetaTags=Produktmodell']
     * @returns {Promise} Promise mit den Produkttabellen im Format {"TableName": [Entries]}
     */
    loadMetadata(query = "tableMetaTags=Produktmodell") {
        let url = Urls.addQsPart(this.wkfgBackendUrl + "metamodell/query", query);
        const lang = Urls.urlParam("lang");
        if (lang) {
            url = Urls.addOrUpdateQsParameter(url, "lang", lang);
        }
        const promise = defaultCatchAsError($.getJSON(url));
        if (this.wkfgTypeConverter) {
            return promise.then((metadata) => {
                this.wkfgTypeConverter.parse(metadata);
                return metadata;
            });
        }
        return promise;
    }

    /**
     * Lädt eine Configuration vom Backend. configurationQuery ist dabei der Query-URL-Teil, der an
     * das Konfiguration-Backend-API weitergeleitet wird
     * @param {string} query
     * @returns {Promise} Promise mit der Konfiguration als Objekt
     */
    loadConfiguration(query) {
        query = Urls.addQsPart("table=Konfiguration&limit=1", query);
        return this.loadMetadata(query).then((data) => _.get(data, "Konfiguration[0]"));
    }

    /**
     * Speichert ein einzelnes Metadata-Objekt
     * @param {string} table wie "Konfiguration"
     * @param {string} action kann sein 'create' oder 'update'
     * @param {Object} metadata das zu speichernde Metadaten-Objekt
     * @param {*} [language=I18N.getLanguage()] optional die Sprache, für die das Metadata-Objekt gespeichert wird
     * @returns {Promise} Promise mit dem Metadaten-Objekt mit vom Server geupdateten Werten (wie id, created_at, updated_at)
     */
    saveMetadata(table, action, metadata, language = I18N.getLanguage()) {
        const url = this.wkfgBackendUrl + `metamodell/${action}/${table}/${language}`;
        const metadataFormatted =
            this.wkfgTypeConverter == null
                ? metadata
                : this.wkfgTypeConverter.format(table, metadata);
        // console.log('metadata',metadata);
        // console.log('metadataFormatted',metadataFormatted);
        return defaultCatchAsWarning(
            $.ajax({
                url,
                type: "POST",
                data: JSON.stringify(metadataFormatted),
                dataType: "json",
                contentType: "application/json",
            })
        );
    }

    /**
     * Speichert globale Settings (key-value-Object) auf dem Server - geht natürlich nur als Admin.
     * Die Settings sollten nur die Settings enthalten, die man überschreiben möchte.
     * Die Settings werden als Table erwartet, wie im Backend angezeigt.
     * Abweichend von saveMetadata werden allerdings nicht die id verwendet, um eventuell vorhandene Einträge zu überschreiben, sondern der Key.
     * Beispiel:
     * saveSettings([{Key:'stage.renderer.isSRGBConversion',Value:'false',Type:'String',Comment:'Sollen Bilder/Farben beim Laden nach SRGB konvertiert werden?'}]);
     * @param {Array<{Key:string,Value:string,Type:string,Comment:string}>}
     */
    saveSettings(settings) {
        const url = this.wkfgBackendUrl + `metamodell/saveSettings`;
        return defaultCatchAsWarning(
            $.ajax({
                url,
                type: "POST",
                data: JSON.stringify(settings),
                dataType: "json",
                contentType: "application/json",
            })
        );
    }

    updatePrices(data) {
        const url = this.wkfgBackendUrl + "price/calculate";
        return defaultCatchAsWarning(
            $.ajax({
                url,
                type: "POST",
                data: JSON.stringify(data),
                dataType: "json",
                contentType: "application/json",
            })
        );
    }

    /**
     * Löscht ein einzelnes Metadata-Objekt mitsamt Content
     * @param {string} table wie "Konfiguration"
     * @param {(number|string)} id
     * @param {boolean} [withContent=true] optional true oder false
     * @returns {Promise} Promise mit Text "ok"
     */
    deleteMetadata(table, id, withContent = true) {
        const url = this.wkfgBackendUrl + `metamodell/delete/${table}/${id}/${withContent}`;
        return defaultCatchAsWarning(
            $.ajax({
                url,
                type: "POST",
                contentType: "application/json",
            })
        );
    }

    /**
     * Leert eine ganze Metadata-Tabelle mitsamt Content
     * @param {string} table wie "Konfiguration"
     * @param {boolean} [withContent=true] optional true oder false
     * @returns {Promise} Promise mit Text "ok"
     */
    truncateMetadata(table, withContent = true) {
        const url = this.wkfgBackendUrl + `metamodell/truncate/${table}/${withContent}`;
        return defaultCatchAsWarning(
            $.ajax({
                url,
                type: "POST",
                contentType: "application/json",
            })
        );
    }

    /**
     * Lädt ein oder mehrere Content-Dateien hoch. Die entsprechenden ContentUploadPatterns müssen im metamodel.json
     * eingetragen sein.
     * @param {string} path das Content-Verzeichnisses (Wurzel ist wkfgcontent), in das Hochgeladen werden soll
     * @param {(Array<{fileName:string,blob:Blob}|File>|{fileName:string,blob:Blob}|File)} files ein {blob,fileName} oder File - Objekt, oder ein Array davon
     * @param {string} [url=this.wkfgBackendUrl + 'content/upload']
     * @returns {Promise} Promise mit der Liste der tatsächlich gespeicherten Server-Dateinamen (die Dateinamen werden normalisiert)
     * @example
     * STAGE.renderToBlob('image/jpg',(blob)=>{
     *     backendApi
     *         .uploadContent(`Konfiguration/${Code}/`, [{fileName:'Vorderansicht.jpg',blob}])
     *         .then(realName=>console.log(realName))
     * });
     */
    uploadContent(path, files, url = this.wkfgBackendUrl + "content/upload") {
        const formData = new FormData();
        formData.append("path", path);
        (_.isArrayLike(files) ? files : [files]).forEach((entry) => {
            if (entry instanceof File) {
                formData.append("file[]", entry, entry.name);
            } else {
                formData.append("file[]", entry.blob, entry.fileName);
            }
        });
        return defaultCatchAsWarning(
            $.ajax({
                url,
                type: "POST",
                data: formData,
                processData: false,
                contentType: false,
            })
        );
    }

    /**
     * Verwendet von APP.augmentedReality
     * Lädt eine GLB mit Projektparameter hoch
     * @param {string} path
     * @param {(Array<{fileName:string,blob:Blob}|File>|{fileName:string,blob:Blob}|File)} files
     * @param {String} project
     * @param {Boolean} convert
     * @returns {Promise}
     */
    augmentedRealityAPI(path, files, project, convert) {
        const url = this.wkfgBackendUrl + "content/augmentedReality";
        const formData = new FormData();
        formData.append("path", path);
        formData.append("project", project);
        formData.append("convert", convert);
        (_.isArrayLike(files) ? files : [files]).forEach((entry) => {
            if (entry instanceof File) {
                formData.append("file", entry, entry.name);
            } else {
                formData.append("file", entry.blob, entry.fileName);
            }
        });
        return defaultCatchAsWarning(
            $.ajax({
                url,
                type: "POST",
                data: formData,
                processData: false,
                contentType: false,
            })
        );
    }

    /**
     * Verwendet von APP.augmentedReality
     * Lädt eine GLB mit Projektparameter hoch
     * @param {string} path
     * @param {(Array<{fileName:string,blob:Blob}|File>|{fileName:string,blob:Blob}|File)} files
     * @param {String} project
     * @param {Number} multipart
     * @param {Number} part
     * @param {Boolean} useRenderfarm
     * @param {String} blendFilename
     * @returns {Promise}
     */
    fotorealisticRenderAPI(path, files, project, multipart, part, useRenderfarm, blendFilename) {
        const url = this.wkfgBackendUrl + "content/fotorealisticRender";
        const formData = new FormData();
        formData.append("path", path);
        formData.append("project", project);
        formData.append("multipart", multipart);
        formData.append("part", part);
        formData.append("useRenderfarm", useRenderfarm);
        formData.append("blendFilename", blendFilename);
        (_.isArrayLike(files) ? files : [files]).forEach((entry) => {
            if (entry instanceof File) {
                formData.append("file", entry, entry.name);
            } else {
                formData.append("file", entry.blob, entry.fileName);
            }
        });
        return defaultCatchAsWarning(
            $.ajax({
                url,
                type: "POST",
                data: formData,
                processData: false,
                contentType: false,
            })
        );
    }

    /**
     * Lädt ein canvasElement als Bild zum Server hoch.
     * Ein Image kann mittels der Images.canvasFromImage Methode in ein Canvas überführt werden
     * @param canvasElement
     * @param {string} path wie "Textures/Holz.jpg"
     * @returns {Promise}
     */
    uploadCanvasImage(canvasElement, path) {
        const mimetype = Mimetypes.getMimetypeOfFilename(path);
        return canvasElement.toBlob((blob) => {
            const uploadPath = path.substringBeforeLast("/");
            const fileName = path.substringAfterLast("/");
            return this.uploadContent(uploadPath, { fileName, blob });
        }, mimetype);
    }

    /**
     * Listet die Dateien eines Content-Verzeichnisses auf
     * @param {string} path Pfad des Content-Verzeichnisses (Wurzel ist wkfgcontent)
     * @returns {Promise}
     */
    listContent(path) {
        let url = Urls.addOrUpdateQsParameter(
            this.wkfgBackendUrl + "content/list",
            "path",
            path.ensureRight("/")
        );
        return defaultCatchAsWarning($.getJSON(url));
    }

    /**
     * Löscht eine einzelne Content-Datei
     * @param {string} path Pfad der Content-Datei (Wurzel ist wkfgcontent)
     * @returns {Promise}
     */
    deleteContent(path) {
        const url = this.wkfgBackendUrl + "content/delete";
        return defaultCatchAsWarning($.post(url, { path }));
    }

    /**
     * Gibt die URL eines PDFs zu einer Konfiguration zurück. Das PDF wird beim Aufruf der URL serverseitig erzeugt.
     * @param {(string|number)} codeOrId Code oder id der Konfiguration
     * @param {boolean} [inline=false] optional: true das PDF wird im Browser angezeigt, false (default) es wird als Datei heruntergeladen
     * @returns {string}
     */
    getPdfUrl(codeOrId, inline = false) {
        let url = `${this.wkfgBackendUrl}pdf/${codeOrId}`; //new UrlFluid(`${this.wkfgBackendUrl}pdf/${codeOrId}`);
        if (inline) {
            url.param("inline", true);
        }
        return url.toString();
    }

    /**
     * sendet eine Mail mit dem PDF an die in der Konfiguration angegebene Mailadresse
     * @returns {Promise}
     */
    getKonfigurationMail() {
        return Urls.addOrUpdateQsParameter(
            this.wkfgBackendUrl + "mail",
            "Code",
            APP.configuration.Code
        );
    }

    /**
     * sendet eine Mail mit dem PDF an die in der Konfiguration angegebene Mailadresse
     * @returns {Promise}
     */
    sendKonfigurationMail() {
        const url = this.getKonfigurationMail();
        return defaultCatchAsWarning($.get(url));
    }
}
